import { Button, Typography } from '@/components/atoms';
import { useTranslation, useUIColors } from '@/core/hooks';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import React from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const GIF_ASPECT = 870 / 1106;
const GIF_WIDTH_COMPACT = 240;
const GIF_WIDTH_WIDE = 280;
/** Figma large-screen 404 block — button and message share this width. */
const BLOCK_WIDTH = 517;
const COMPACT_BREAKPOINT = 768;

/**
 * Shared 404 for web and native: GIF illustration, title, subtitle, home CTA.
 * Compact: copy centered, button pinned to the bottom. Wide: stacked 517px block.
 */
export function NotFoundScreen() {
    const { t } = useTranslation();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { width: screenWidth } = useWindowDimensions();
    const isCompact = screenWidth < COMPACT_BREAKPOINT;
    const pagePadding = isCompact ? 16 : 24;
    const gifWidth = isCompact ? GIF_WIDTH_COMPACT : GIF_WIDTH_WIDE;
    const gifHeight = Math.round(gifWidth * GIF_ASPECT);

    return (
        <View
            style={[
                styles.page,
                isCompact ? styles.pageCompact : styles.pageWide,
                {
                    backgroundColor: colors.backgroundLevel2,
                    paddingTop: Math.max(insets.top, pagePadding),
                    paddingBottom: Math.max(insets.bottom, pagePadding),
                    paddingHorizontal: pagePadding,
                },
            ]}
        >
            <View
                style={[
                    styles.body,
                    isCompact ? styles.bodyCompact : styles.bodyWide,
                ]}
            >
                <Image
                    source={require('@assets/images/gif/not-found-404.gif')}
                    style={{
                        width: gifWidth,
                        height: gifHeight,
                        backgroundColor: colors.backgroundLevel2,
                    }}
                    contentFit="contain"
                />
                <View style={styles.copy}>
                    <Typography
                        variant="title2"
                        color="fontHighContrast"
                        style={styles.title}
                    >
                        {t('errors.pageNotFound')}
                    </Typography>
                    <Typography
                        variant="body"
                        color="fontMuted"
                        style={styles.subtitle}
                    >
                        {t('errors.pageNotFoundDescription')}
                    </Typography>
                </View>
            </View>
            <View style={[styles.cta, !isCompact && styles.ctaWide]}>
                <Button
                    title={t('errors.backToHome')}
                    onPress={() => router.replace('/')}
                    variant="primary"
                    fullWidth
                    style={[
                        styles.button,
                        { backgroundColor: colors.fontHighContrast },
                    ]}
                    textStyle={[styles.buttonLabel, { color: colors.white }]}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        alignItems: 'center',
    },
    pageCompact: {
        justifyContent: 'flex-start',
    },
    pageWide: {
        justifyContent: 'center',
        gap: 24,
    },
    body: {
        width: '100%',
        alignItems: 'center',
        gap: 24,
    },
    bodyCompact: {
        flex: 1,
        justifyContent: 'center',
    },
    bodyWide: {
        maxWidth: BLOCK_WIDTH,
    },
    copy: {
        alignItems: 'center',
        gap: 8,
        width: '100%',
    },
    title: {
        width: '100%',
        textAlign: 'center',
        fontSize: 22,
        lineHeight: 22,
        fontFamily: 'Matter-SemiBold',
        letterSpacing: 0,
    },
    subtitle: {
        width: '100%',
        textAlign: 'center',
        fontSize: 16,
        lineHeight: 22,
        fontFamily: 'Matter-Regular',
        letterSpacing: 0,
    },
    cta: {
        width: '100%',
    },
    ctaWide: {
        maxWidth: BLOCK_WIDTH,
    },
    button: {
        width: '100%',
        height: 48,
        borderRadius: 4, // Spacing/1
        padding: 12, // Spacing/3
        gap: 4,
    },
    buttonLabel: {
        fontFamily: 'Matter-SemiBold',
    },
});
